import React, { useEffect, useRef, useState } from 'react'
import $t from '../../../i18n'
import { useDispatch, useSelector } from 'react-redux'
import Actions from '../../../actions'
import ProcessFailZouraDialog from '../dialogs/ProcessFailZouraDialog/ProcessFailZouraDialog'
import { routes } from '../../../consts'
import Page from '../../common/Page'
import PaymentDetailsCard from '../../common/PaymentDetailsCard'
import config from '../../../config.json'
import Card from '../../common/Card'
import {
  isHmoPaymentFlow,
  shouldShowProcessingDisclaimer
} from '../../../utils/hmoUtils'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { getUrlParams } from '../../../utils/useParamUtil'
import { event, eventTypes } from '../../../consts/eventConsts'
import { Button, Switch } from 'antd'
import { getUserSubscription } from '../../../utils/paymentUtils'
import PaymentRow from './PaymentRow/PaymentRow'

import './ZouraBillingForm.css'

// This zoura billing form is used to display the billing form for the user to pay for their subscription.
// It uses the zoura iframe to display the payment form.
// It also displays the subscription details of the user.
// There are tree types of users that can use this form:
// 1. Unauthenticated payment user
// 2. HMO member
// 3. Telehealth user
// The form will display the subscription details of the user based on the user type.
// Please make sure to use safe get here to avoid any errors (because of user type switching).
const ZouraBillingForm = () => {
  // Supporting verious date formats in Safari
  dayjs.extend(customParseFormat)

  const dispatch = useDispatch()
  const { memberId, subscriptionId, token, transactionId } = getUrlParams()
  const zouraIframeRef = useRef([])

  const {
    future_subscriptions,
    my_subscriptions,
    zuoraSignatureDetails,
    selectedPlan,
    customerCoupon,
    paymentDetailsData,
    unauthenticatedPaymentDetails,
    isLoadingCheckoutPage,
    paymentAddressObj,
    totalPaymentPrice,
    balancePerSubscription
  } = useSelector(state => state.payment)

  const isSubmitLoading = useSelector(state => state.payment.zouraPageLoader)

  const userDetails = useSelector(state => state.profile.userDetails)

  const userHomeAddressState = useSelector(
    state => state.account?.userHomeAddressState
  )

  const [paymentSummary, setPaymentSummary] = useState(null)

  const includeDue = useSelector(state => state.payment.includeDueInvoices)

  const updateIncludeDue = status => {
    dispatch(Actions.createAction(Actions.UPDATE_INCLUDE_DUE_STATUS, status))
  }

  const isCheckOptionSelected =
    paymentSummary?.paymentMethodType ===
    $t('pages.paymentsAndBilling.checkOption')

  const isUnautenticatedPayment = memberId && subscriptionId && !userDetails

  // Pay-by-check already shows its own "5-8 business days" note on this same
  // screen; the "48 hours" ACK-confirmation disclaimer only applies to the
  // electronic payment methods it was written for, so it's suppressed here.
  const showProcessingDisclaimer =
    shouldShowProcessingDisclaimer(userDetails, unauthenticatedPaymentDetails) &&
    !isCheckOptionSelected

  // hmo member can be also unauthenticated payment
  const isHmoPayment =
    isUnautenticatedPayment || isHmoPaymentFlow(userDetails, selectedPlan)

  useEffect(() => {
    if (isUnautenticatedPayment && !unauthenticatedPaymentDetails) {
      dispatch(
        Actions.createAction(Actions.UNAUTENTICATED_PAYMENT_DETAILS_REQUESTED, {
          memberId,
          subscriptionId,
          token,
          transactionId
        })
      )
    }
  }, [isUnautenticatedPayment])

  useEffect(() => {
    if (isHmoPayment && !isUnautenticatedPayment) {
      if (paymentSummary) {
        dispatch(
          Actions.createAction(Actions.GENERATE_PAYMENT_PAGE_DATA_REQUESTED, {
            selectedPlan: null,
            customerCoupon: null
          })
        )
      }
    }
  }, [isHmoPayment, isUnautenticatedPayment, paymentSummary, dispatch])

  const accountSummary = paymentDetailsData?.accountSummary

  const { price, no_of_billing_period } = zuoraSignatureDetails

  const pages = unauthenticatedPaymentDetails
    ? unauthenticatedPaymentDetails?.pages
    : zuoraSignatureDetails?.pages || [zuoraSignatureDetails]

  const paymentSubscription = getUserSubscription(
    my_subscriptions,
    future_subscriptions
  )

  const paymentPlanDetails = isUnautenticatedPayment
    ? {
        planPrice: includeDue
          ? unauthenticatedPaymentDetails?.balance?.totalBalance
          : unauthenticatedPaymentDetails?.balance?.overdueBalance,
        overdueBalance: unauthenticatedPaymentDetails?.balance.overdueBalance,
        dueBalance: unauthenticatedPaymentDetails?.balance.dueBalance,
        dueDate: unauthenticatedPaymentDetails?.balance?.latestInvoiceDate,
        planName: unauthenticatedPaymentDetails?.plan_name?.split('(')?.[0],
        planDescription: unauthenticatedPaymentDetails?.plan_name
          ?.split('(')?.[1]
          ?.replace(')', ''),
        isAutoPay: true,
        effectiveStartDate: dayjs(
          unauthenticatedPaymentDetails?.effective_begin_date,
          'MM/DD/YYYY'
        )
      }
    : {
        planPrice: isHmoPayment
          ? includeDue
            ? accountSummary?.totalBalance
            : accountSummary?.overdueBalance
          : selectedPlan?.price,
        planName: isHmoPayment
          ? paymentSubscription?.plan_name?.split('(')?.[0]
          : selectedPlan?.plan_name || selectedPlan?.title,
        planDescription: isHmoPayment
          ? paymentSubscription?.plan_name?.split('(')?.[1]?.replace(')', '')
          : null,
        overdueBalance: isHmoPayment ? accountSummary?.overdueBalance : null,
        dueBalance: isHmoPayment ? accountSummary?.dueBalance : null,
        invoiceNumber: accountSummary?.invoiceNumber,
        invoiceDate: accountSummary?.invoiceDate,
        dueDate: accountSummary?.dueDate,
        isAutoPay: isHmoPayment
          ? accountSummary?.isAutoPay
          : selectedPlan?.payment_every,
        isCouponApplied: isHmoPayment
          ? false
          : typeof customerCoupon === 'string' && customerCoupon,
        effectiveStartDate: isHmoPayment
          ? userDetails?.hmo_effective_begin_date
          : null
      }

  // Get sorted subscriptions by effectiveInvoicePeriodDate
  const getSortedSubscriptions = () => {
    if (!balancePerSubscription?.length) return []

    return [...balancePerSubscription].sort((a, b) => {
      // Sort by effectiveInvoicePeriodDate - earliest invoice = current subscription, later invoice = future subscription
      const dateA = new Date(a.effectiveInvoicePeriodDate || '1900-01-01')
      const dateB = new Date(b.effectiveInvoicePeriodDate || '1900-01-01')
      return dateA - dateB
    })
  }

  // Function to create subscription payment details list from sorted subscriptions
  const createSubscriptionPaymentDetailsList = () => {
    const sortedSubscriptions = getSortedSubscriptions()
    if (!sortedSubscriptions.length) return []

    return sortedSubscriptions.map(subscription => ({
      subscriptionId: subscription.subscriptionId,
      planPrice: includeDue
        ? subscription.totalPrice
        : subscription.overduePrice,
      overdueBalance: subscription.overduePrice,
      dueBalance: subscription.duePrice,
      totalPrice: subscription.totalPrice,
      dueDate: subscription.lastInvoiceDate,
      invoiceDate: subscription.lastInvoiceDate,
      invoiceNumber: subscription.lastInvoiceNumber,
      planName: subscription.planName?.split('(')?.[0] || subscription.planName,
      planDescription: subscription.planName?.split('(')?.[1]?.replace(')', ''),
      isAutoPay: accountSummary?.isAutoPay,
      isCouponApplied: false,
      effectiveStartDate: userDetails?.hmo_effective_begin_date,
      effectiveInvoicePeriodDate: subscription.effectiveInvoicePeriodDate
    }))
  }

  // Only for HMO users with balancePerSubscription data
  const subscriptionPaymentDetailsList =
    isHmoPayment && balancePerSubscription?.length > 0
      ? createSubscriptionPaymentDetailsList()
      : []

  const hasMultipleSubscriptions = balancePerSubscription?.length > 1

  const totalPriceToPay = isHmoPayment
    ? totalPaymentPrice
    : paymentPlanDetails?.planPrice || 0

  const hasOverdueBalance = () => {
    return isHmoPayment && paymentPlanDetails?.overdueBalance > 0
  }

  const hasDueBalance = () => {
    return isHmoPayment && paymentPlanDetails?.dueBalance > 0
  }
  const onPaymentMethodSuccess = payload => {
    // set payment summary to show before payment
    isHmoPayment
      ? setPaymentSummary({
          ...payload,
          memberId: userDetails?.hmo_member_id || memberId,
          subscriptionId: paymentSubscription?.id || subscriptionId,
          paymentMethodId: payload?.refId,
          isHMOMember: true
        })
      : // for telehealth user no need to show payment summary
        dispatchPaymentOrder(payload)
  }

  const dispatchCheckOption = () => {
    unauthenticatedPaymentDetails
      ? // if unauthenticated payment, then navigate to payment success page
        // with check=true query param
        dispatch(
          Actions.createAction(
            Actions.NAVIGATED_TO,
            routes.PAYMENT_SUCCESS + '?check=true'
          )
        )
      : dispatch(
          Actions.createAction(Actions.NAVIGATED_TO, routes.MENU_ROUTE.PAYMENTS)
        )
  }

  const dispatchPaymentOrder = payload => {
    dispatch(Actions.createAction(Actions.ENABLE_ZOURA_PAGE_LOADER))
    dispatch(Actions.createAction(Actions.ADD_PAYMENT_METHOD_SUCCEED, payload))
    dispatch(
      Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
        type: eventTypes.formEvent,
        eventName: event.formEvent.submit,
        data: payload,
        element: Actions.ADD_PAYMENT_METHOD_SUCCEED
      })
    )
  }

  const onCloseFailDialog = () => {
    if (isUnautenticatedPayment) {
      dispatch(
        Actions.createAction(Actions.UNAUTENTICATED_PAYMENT_DETAILS_REQUESTED, {
          memberId,
          subscriptionId,
          token,
          transactionId
        })
      )
    } else {
      dispatch(Actions.createAction(Actions.NAVIGATED_TO, routes.ROOT))
      dispatch(Actions.createAction(Actions.CLEAR_ZOURA_SIGNATURES))
    }
  }

  if (
    !pages?.filter(page => !!page.signature).length &&
    !isLoadingCheckoutPage
  ) {
    return (
      <>
        <h1 className='zoura-no-signature-heading'>
          {$t('pages.billing.noSignatureTitle')}{' '}
        </h1>
        <p
          className='zoura-no-signature-back'
          onClick={() =>
            dispatch(Actions.createAction(Actions.NAVIGATED_TO, routes.ROOT))
          }
        >
          {' '}
          {$t('pages.billing.backToHomepage')}
        </p>
      </>
    )
  }
  return (
    <Page
      className='zoura-page'
      title={
        isUnautenticatedPayment
          ? ''
          : paymentSummary
          ? $t('pages.billing.paymentSummary.title')
          : $t('pages.paymentsAndBilling.title')
      }
      loading={isLoadingCheckoutPage}
    >
      {isHmoPayment &&
      hasMultipleSubscriptions &&
      subscriptionPaymentDetailsList.length > 0 ? (
        <Card className='payment-subscription-details-card'>
          <>
            {subscriptionPaymentDetailsList.map((planDetails, index) => (
              <PaymentRow
                key={planDetails.subscriptionId || index}
                paymentPlanDetails={planDetails}
                editable={!paymentSummary}
              />
            ))}
          </>
          <div className='price-details section'>
            <div
              className={
                paymentPlanDetails?.isCouponApplied &&
                paymentPlanDetails?.isAutoPay
                  ? 'price-details-field'
                  : 'price-details-field row'
              }
            >
              <span className='price-text'>
                {$t('pages.billing.totalPayment')}:
              </span>
              {!paymentPlanDetails?.isCouponApplied && (
                <span className='price-value'>
                  ${parseFloat(totalPriceToPay).toFixed(2)}{' '}
                </span>
              )}
              {paymentPlanDetails?.isCouponApplied && (
                <h1 className='price-value semi-bold'>
                  <s>$ {paymentPlanDetails?.planPrice}</s> ${price}
                  {paymentPlanDetails?.isAutoPay &&
                    $t('pages.billing.untilCoupon')}
                </h1>
              )}
            </div>
          </div>
        </Card>
      ) : (
        <Card className='payment-subscription-details-card'>
          {isHmoPayment && !isUnautenticatedPayment && (
            <div className='invoice-details section'>
              <div
                className={
                  paymentSummary ? 'invoice-field streach' : 'invoice-field'
                }
              >
                <div className='invoice-label'>
                  {$t('pages.billing.invoiceNumber')}
                </div>
                <div className='invoice-value semi-bold'>
                  {paymentPlanDetails?.invoiceNumber}
                </div>
              </div>
              {!paymentSummary && (
                <div className='invoice-field'>
                  <div className='invoice-label'>
                    {$t('pages.billing.invoiceDate')}
                  </div>
                  <div className='invoice-value semi-bold'>
                    {dayjs(paymentPlanDetails?.invoiceDate).format(
                      'MMMM D, YYYY'
                    )}
                  </div>
                </div>
              )}
              {!paymentSummary && (
                <div className='invoice-field'>
                  <div className='invoice-label'>
                    {$t('pages.billing.dueDate')}
                  </div>
                  <div className='invoice-value semi-bold'>
                    {dayjs(paymentPlanDetails?.dueDate).format('MMMM D, YYYY')}
                  </div>
                </div>
              )}
            </div>
          )}
          <div className='name-details section'>
            <div className='semi-bold'>{paymentPlanDetails?.planName}</div>
            {paymentPlanDetails?.planDescription && (
              <div className='description'>
                {paymentPlanDetails?.planDescription}
              </div>
            )}
          </div>
          {paymentPlanDetails?.effectiveStartDate && (
            <div className='plan-effective-date section'>
              <div className='semi-bold'>
                {$t('pages.billing.coverageEffectiveNote', {
                  date: dayjs(paymentPlanDetails?.effectiveStartDate).format(
                    config.genericDateFormat
                  )
                })}
              </div>
            </div>
          )}
          {paymentPlanDetails?.isAutoPay &&
            paymentPlanDetails?.isCouponApplied &&
            no_of_billing_period && (
              <div className='coupon-details section'>
                <div className='semi-bold'>
                  {$t('pages.billing.couponDiscount')}
                </div>
                <span>
                  $
                  {parseFloat(
                    paymentPlanDetails?.planPrice &&
                      paymentPlanDetails?.planPrice - price
                  ).toFixed(2)}
                  {$t('pages.billing.off')} {no_of_billing_period}
                  {no_of_billing_period > 1
                    ? $t('pages.billing.months')
                    : $t('pages.billing.month')}
                </span>
              </div>
            )}
          {hasOverdueBalance() && (
            <div className='price-details section'>
              <>
                <div className='amount-box'>
                  <div>
                    ${parseFloat(paymentPlanDetails?.overdueBalance).toFixed(2)}
                    <span className='amount-info-text'>
                      {$t('pages.billing.overdueAmountRow')}
                    </span>
                  </div>
                  <span>{$t('pages.billing.required')}</span>
                </div>
                {hasDueBalance() && (
                  <div className='amount-box'>
                    <div>
                      ${parseFloat(paymentPlanDetails?.dueBalance).toFixed(2)}
                      <span className='amount-info-text'>
                        {$t('pages.billing.amountDueBy')}{' '}
                        {dayjs(paymentPlanDetails?.dueDate).format(
                          'MMMM D, YYYY'
                        )}
                      </span>
                    </div>
                    <Switch checked={includeDue} onChange={updateIncludeDue} />
                  </div>
                )}
              </>
            </div>
          )}
          <div className='price-details section'>
            <div
              className={
                paymentPlanDetails?.isCouponApplied &&
                paymentPlanDetails?.isAutoPay
                  ? 'price-details-field'
                  : 'price-details-field row'
              }
            >
              <span className='price-text'>
                {paymentPlanDetails?.isAutoPay //unauthenticated flow - autopay local var is always true
                  ? isHmoPayment
                    ? $t('pages.billing.totalPayment')
                    : $t('pages.billing.monthlyAmount')
                  : $t('pages.billing.totalPayment')}
                :
              </span>
              {!paymentPlanDetails?.isCouponApplied && (
                <span className='price-value'>
                  ${parseFloat(paymentPlanDetails?.planPrice).toFixed(2)}{' '}
                </span>
              )}
              {paymentPlanDetails?.isCouponApplied && (
                <h1 className='price-value semi-bold'>
                  <s>$ {paymentPlanDetails?.planPrice}</s> ${price}
                  {paymentPlanDetails?.isAutoPay &&
                    $t('pages.billing.untilCoupon')}
                </h1>
              )}
            </div>
          </div>
        </Card>
      )}

      {!paymentSummary && (
        <PaymentDetailsCard
          isEditableView={true}
          zouraIframeRef={zouraIframeRef}
          onSuccess={onPaymentMethodSuccess}
          SubmitButtonText={
            isHmoPayment
              ? $t('pages.billing.continuePayment')
              : $t('pages.billing.confirmPayment')
          }
          disabled={totalPriceToPay === 0}
          unauthenticatedPaymentDetails={unauthenticatedPaymentDetails}
        />
      )}
      {paymentSummary && (
        <>
          <Card className='payment-summary-card'>
            <div className='payment-summary-sections'>
              <div className='payment-summary-section'>
                <div className='payment-summary-label semi-bold'>
                  {$t(`pages.paymentDetails.method`)}:
                </div>
                <div className='payment-summary-value'>
                  {$t(
                    `pages.paymentsAndBilling.${paymentSummary?.paymentMethodType}`,
                    {
                      defaults: [
                        {
                          message: paymentSummary?.paymentMethodType
                        }
                      ]
                    }
                  )}
                </div>
              </div>
              {!isCheckOptionSelected && (
                <div className='payment-summary-section'>
                  <div className='payment-summary-label semi-bold'>
                    {$t(`pages.paymentDetails.billingAddress`)}:
                  </div>
                  <div className='payment-summary-value'>
                    <div className='value'>
                      {paymentAddressObj?.creditCardAddress1}{' '}
                    </div>
                    <div className='value'>
                      {' '}
                      {paymentAddressObj?.creditCardAddress2}{' '}
                    </div>
                    <div className='value'>
                      {paymentAddressObj?.creditCardCity},{' '}
                      {paymentAddressObj?.creditCardState}{' '}
                      {paymentAddressObj?.creditCardPostalCode}
                    </div>
                  </div>
                </div>
              )}
              {!isCheckOptionSelected && (
                <div className='payment-summary-section'>
                  <div className='payment-summary-label semi-bold'>
                    {paymentSummary?.autoPay
                      ? $t('pages.billing.paymentSummary.autopay')
                      : $t('pages.billing.paymentSummary.onTime')}
                  </div>
                  {paymentSummary?.autoPay && (
                    <div className='payment-summary-value'>
                      {$t('pages.billing.paymentSummary.autopayDesc')}
                    </div>
                  )}
                </div>
              )}
              {isCheckOptionSelected && (
                <div className='payment-summary-section'>
                  <div className='payment-summary-label semi-bold'>
                    {$t('pages.billing.paymentSummary.check')}
                  </div>
                  <div className='payment-summary-value'>
                    {$t('pages.billing.paymentSummary.checkDesc', {
                      state: unauthenticatedPaymentDetails
                        ? unauthenticatedPaymentDetails?.address?.state
                        : userHomeAddressState
                    })}
                  </div>
                </div>
              )}
            </div>
          </Card>
          {showProcessingDisclaimer && (
            <div className='payment-processing-disclaimer'>
              {$t('pages.billing.paymentSummary.processingDisclaimer')}
            </div>
          )}
          <div className='payment-summary-button'>
            <Button
              className='payment-submit'
              type='primary'
              loading={isSubmitLoading}
              onClick={() =>
                isCheckOptionSelected
                  ? dispatchCheckOption()
                  : dispatchPaymentOrder(paymentSummary)
              }
            >
              {isCheckOptionSelected
                ? $t('pages.paymentsAndBilling.checkAction')
                : $t('pages.billing.confirmPayment')}
            </Button>
            {paymentSummary && (
              <Button
                type='link'
                disabled={isSubmitLoading}
                className='payment-edit-btn'
                onClick={() => {
                  setPaymentSummary(null)
                  dispatch(
                    Actions.createAction(
                      Actions.GENERATE_PAYMENT_PAGE_DATA_REQUESTED,
                      {
                        selectedPlan: null,
                        customerCoupon: null
                      }
                    )
                  )
                }}
              >
                {$t('pages.billing.editPayment')}
              </Button>
            )}
          </div>
        </>
      )}
      <ProcessFailZouraDialog onClose={onCloseFailDialog} />
    </Page>
  )
}

export default ZouraBillingForm
